import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, LogOut, User } from 'lucide-react';

/**
 * Sticky amber banner shown ONLY inside an admin impersonation window
 * (i.e., when the URL was opened via "Log in as User" from the admin tab).
 *
 * Closing the tab/window ends the impersonation session — the admin's
 * original tab is in a completely separate window and is untouched.
 */
const ImpersonationBanner = () => {
  const { isImpersonationWindow, impersonationTarget, returnToAdmin } = useAuth();
  const [closing, setClosing] = useState(false);

  if (!isImpersonationWindow) return null;

  const target = impersonationTarget || {};

  return (
    <div
      className="sticky top-0 z-[100] w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg border-b-2 border-amber-700"
      data-testid="impersonation-banner"
      role="alert"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div className="text-sm leading-tight min-w-0">
            <div className="font-bold tracking-wide uppercase text-xs opacity-90">
              Admin Impersonation Window
            </div>
            <div className="truncate">
              Logged in as{' '}
              <span className="font-semibold underline decoration-white/40" data-testid="impersonation-target">
                {target.name || target.email || 'this user'}
              </span>
              <span className="opacity-90"> · {target.email}</span>
              {target.adminName && (
                <span className="opacity-75 hidden md:inline"> · opened by {target.adminName}</span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            setClosing(true);
            returnToAdmin();
          }}
          disabled={closing}
          className="flex items-center gap-2 bg-white text-orange-700 hover:bg-amber-50 disabled:opacity-60 px-4 py-1.5 rounded-md font-semibold text-sm shadow-sm transition-colors whitespace-nowrap"
          data-testid="impersonation-return-btn"
          title="Close this impersonation window — your admin tab is still open"
        >
          {closing ? (
            <>
              <User className="h-4 w-4 animate-pulse" /> Closing…
            </>
          ) : (
            <>
              <LogOut className="h-4 w-4" /> End Session
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
